import template from './ko-fields.html';

/**
 * ViewModel for the ko-fields component.
 * Manages field definitions, values, and filtering logic for dynamic field rendering.
 * @class FieldsComponentViewModel
 */
class FieldsComponentViewModel
{
	/**
	 * Creates an instance of FieldsComponentViewModel.
	 * @param {Object} params - The component parameters
	 * @param {Object} params.values - The initial field values object
	 * @param {Array} [params.definitions] - Array of field definitions. Optional
	 * @param {Array} [params.filter_table_uuids] - Array of table UUIDs to filter by
	 * @param {Array} [params.filter_schemas] - Array of schema names to filter by
	 * @param {Array} [params.filter_tables] - Array of table names to filter by
	 * @param {boolean} [params.strict_mode=true] - Optional.Whether to use strict filtering mode. When set to true, no provided table or uuid filters will result in no fields showing.
	 * @param {boolean} [params.force_qnames=false] - Whether to force the use of qnames for table names.
	 * @param {integer} [auto_size=false] - Optional. Whether to automatically size the columns to the content. Used mainly in table subrows.
	 * @param {boolean} [params.merge_sections=false] - Optional. Whether to merge sections with the same table name.
	 */
	constructor(params)
	{
		this.params = params;

		this.all_definitions = ko.observableArray();
		this.internal_values = ko.observable();

		this.values = ko_helper.safe_observable(params.values, {});
		this.auto_size = ko_helper.safe_observable(params.auto_size, false);

		this.incoming_definitions = ko_helper.safe_observableArray(params.definitions, []);
		this.filter_table_uuids = ko_helper.safe_observableArray(params.filter_table_uuids, []);
		this.filter_schemas = ko_helper.safe_observable(params.filter_schemas, []);
		this.filter_tables = ko_helper.safe_observableArray(params.filter_tables, []);
		this.strict_mode = ko_helper.safe_observable(params.strict_mode, true);
		this.force_qnames = ko_helper.safe_observable(params.force_qnames, false);
		this.readonly = ko_helper.safe_observable(params.readonly, false);
		this.merge_sections = ko_helper.safe_observable(params.merge_sections, false);

		this.effective_definitions = ko.pureComputed(() =>
		{
			let definitions = this.incoming_definitions() ?? [];
			if (definitions.length == 0)
			{
				definitions = this.all_definitions() ?? [];

				if (this.strict_mode())
				{
					if (this.filter_table_uuids().length == 0 && this.filter_tables().length == 0)
					{
						definitions = [];
					}
				}

				if (this.filter_table_uuids().length > 0)
				{
					definitions = definitions.filter(def => this.filter_table_uuids().indexOf(def?.fields_table_uuid) >= 0);
				}

				if (this.filter_schemas() && this.filter_schemas().length > 0)
				{
					definitions = definitions.filter(def => this.filter_schemas().indexOf(def?.schema) >= 0);
				}

				if (this.filter_tables() && this.filter_tables().length > 0)
				{
					definitions = definitions.filter(def =>
						this.filter_tables().indexOf(def?.table) >= 0
						|| this.filter_tables().indexOf(`${def?.schema}.${def?.table}`) >= 0
					);
				}
			}

			definitions.sort((a, b) => a?.idx - b?.idx);
			return definitions;
		});

		// Create a map of aliases to use for table name lookup. Provided for backwards compatibility so qname is only used when necessary, unless force_qnames is true.
		this.aliases = ko.pureComputed(() =>
		{
			let aliases = {};

			this.effective_definitions().forEach(def =>
			{
				let qname = `${def?.schema}.${def?.table}`;
				let alias = def?.table;

				if (aliases[alias] || this.force_qnames())
					alias = qname;

				aliases[qname] = alias;
			});

			return aliases;
		});

		this.aliases.subscribe(() =>
		{
			this.init_fields(this.effective_definitions());
		});

		// Re-initialize fields when readonly changes
		this.readonly.subscribe(() =>
		{
			this.init_fields(this.effective_definitions());
		});

		this.updateData();
	}

	/**
	 * Updates the all_definitions observable by fetching field table definitions from cache.
	 * @async
	 * @returns {Promise<void>}
	 */
	async updateData()
	{
		this.all_definitions(await Grape.cache.get('FieldTables'));
	}

	/**
	 * Initializes internal field observables from field definitions.
	 * Creates observable values for each field in each section and sets up subscriptions. Throttled updates to 500ms to prevent excessive re-renders.
	 * @param {Array} definitions - Array of field definitions to initialize
	 */
	init_fields(definitions)
	{
		let internal_values = {};
		let val = this.values();

		definitions?.forEach(section =>
		{
			if (section.fields)
			{
				let qname = `${section.schema}.${section.table}`;
				let alias = this.aliases()[qname];

				internal_values[alias] = {};
				let table_values = internal_values[alias];

				section.fields.forEach(field =>
				{
					table_values[field.name] = ko.observable(val?.[alias]?.[field.name] ?? null).extend({ throttle: 20 });
					table_values[field.name].subscribe(() =>
					{
						this.updateValues();
					});

					// Set readonly on field if component is readonly
					if (this.readonly())
						field.readonly = true;
					else
						field.readonly = false;
				});
			}
		});

		this.internal_values(internal_values);
		this.updateValues();
	}

	/**
	 * Checks if a field exists in the internal values structure.
	 * @param {Object} definition - The field definition containing schema and table
	 * @param {Object} field - The field object to check
	 * @returns {boolean} True if the field exists, false otherwise
	 */
	field_exists(definition, field)
	{
		let qname = `${definition.schema}.${definition.table}`;
		let table_alias = this.aliases()[qname];
		let table_values = this.internal_values()?.[table_alias];

		return this.internal_values() && table_values && ko.isObservable(table_values[field.name]);
	}

	/**
	 * Helper function to get the value of a field from the internal values structure.
	 * @param {Object} definition - The field definition containing schema and table
	 * @param {Object} field - The field object to get the value of
	 * @returns {Observable} The observable value of the field
	 */
	get_value(definition, field)
	{
		let qname = `${definition.schema}.${definition.table}`;
		let table_alias = this.aliases()[qname];
		let table_values = this.internal_values()?.[table_alias];

		return table_values[field.name] ?? null;
	}

	/**
	 * Determines if a definition has any fields to display.
	 * @param {Object} definition - The field definition containing schema and table
	 * @returns {boolean} True if the definition has fields to render, false otherwise
	 */
	section_has_fields(definition)
	{
		if (!definition?.schema || !definition?.table)
			return false;

		let qname = `${definition.schema}.${definition.table}`;
		let table_alias = this.aliases()[qname];
		let table_values = this.internal_values()?.[table_alias];

		return !!(table_values && Object.keys(table_values).length > 0);
	}

	/**
	 * Updates the values observable by reading all current values from internal observables.
	 * Includes all fields even if they have null values.
	 */
	updateValues()
	{
		let values = {};
		let internal_values = this.internal_values();

		// Ensure all fields are included, even if they have null values
		for (let table_name in internal_values)
		{
			values[table_name] = {};
			for (let field_name in internal_values[table_name])
			{
				// Get the current value, preserving null/undefined values
				let value = internal_values[table_name][field_name]();
				values[table_name][field_name] = value;
			}
		}

		this.values(values);
	}
}

export default {
	name: 'ko-fields',
	viewModel: FieldsComponentViewModel,
	module_type: 'ko',
	template: template
};
